import { format } from 'date-fns';
import { PlaceholderKey, isKnownPlaceholderKey } from './placeholder-keys';
import type { PlaceholderData } from './types';

export interface ResolverTeam {
  id: number;
  name: string;
  shortName: string;
  logoUrl?: string;
}

export interface ResolverChampionship {
  id: number;
  name: string;
  logoUrl?: string;
  startDate?: string;
}

export interface ResolverTournament {
  id: number;
  name: string;
}

export interface ResolverMatch {
  id: number;
  date?: string;
  venue?: string;
  homeScore?: number;
  awayScore?: number;
}

export interface ResolverPlayer {
  id: number;
  name: string;
  number?: number;
  photoUrl?: string;
}

export interface ResolverContext {
  tournament: ResolverTournament;
  championship: ResolverChampionship;
  match: ResolverMatch;
  homeTeam: ResolverTeam;
  awayTeam: ResolverTeam;
  homeRoster?: ResolverPlayer[];
  awayRoster?: ResolverPlayer[];
}

export type ResolvedValue =
  | { type: 'text'; value: string }
  | { type: 'image'; url: string };

export function resolvePlaceholder(data: PlaceholderData, ctx: ResolverContext): ResolvedValue | null {
  if (!isKnownPlaceholderKey(data.placeholderKey)) return null;
  const key = data.placeholderKey as PlaceholderKey;
  const teamSlot = pickTeam(data.teamNumber, ctx);

  switch (key) {
    case PlaceholderKey.TEAM_NAME:
      return teamSlot ? text(teamSlot.name) : null;
    case PlaceholderKey.TEAM_SHORT_NAME:
      return teamSlot ? text(teamSlot.shortName) : null;
    case PlaceholderKey.TEAM_LOGO:
      return teamSlot?.logoUrl ? image(teamSlot.logoUrl) : null;
    case PlaceholderKey.SCORE: {
      if (!data.teamNumber) {
        return text(`${ctx.match.homeScore ?? 0}:${ctx.match.awayScore ?? 0}`);
      }
      const score = data.teamNumber === 1 ? ctx.match.homeScore : ctx.match.awayScore;
      return text(String(score ?? 0));
    }
    case PlaceholderKey.CHAMPIONSHIP_NAME:
      return text(ctx.championship.name);
    case PlaceholderKey.CHAMPIONSHIP_LOGO:
      return ctx.championship.logoUrl ? image(ctx.championship.logoUrl) : null;
    case PlaceholderKey.TOURNAMENT_NAME:
      return text(ctx.tournament.name);
    case PlaceholderKey.MATCH_DATE:
      return ctx.match.date ? text(format(new Date(ctx.match.date), 'dd.MM.yyyy')) : null;
    case PlaceholderKey.MATCH_TIME:
      return ctx.match.date ? text(format(new Date(ctx.match.date), 'HH:mm')) : null;
    case PlaceholderKey.MATCH_VENUE:
      return ctx.match.venue ? text(ctx.match.venue) : null;
    case PlaceholderKey.PLAYER_NAME: {
      const player = pickPlayer(data.teamNumber, data.playerNumber, ctx);
      return player ? text(player.name) : null;
    }
    case PlaceholderKey.PLAYER_NUMBER: {
      const player = pickPlayer(data.teamNumber, data.playerNumber, ctx);
      return player && typeof player.number === 'number' ? text(String(player.number)) : null;
    }
    case PlaceholderKey.PLAYER_PHOTO: {
      const player = pickPlayer(data.teamNumber, data.playerNumber, ctx);
      return player?.photoUrl ? image(player.photoUrl) : null;
    }
  }

  return null;
}

function pickTeam(teamNumber: number | undefined, ctx: ResolverContext): ResolverTeam | undefined {
  if (teamNumber === 1) return ctx.homeTeam;
  if (teamNumber === 2) return ctx.awayTeam;
  return undefined;
}

function pickPlayer(
  teamNumber: number | undefined,
  playerNumber: number | undefined,
  ctx: ResolverContext,
): ResolverPlayer | undefined {
  if (!playerNumber || playerNumber < 1) return undefined;
  const roster = teamNumber === 1 ? ctx.homeRoster : teamNumber === 2 ? ctx.awayRoster : undefined;
  return roster?.[playerNumber - 1];
}

function text(value: string): ResolvedValue {
  return { type: 'text', value };
}

function image(url: string): ResolvedValue {
  return { type: 'image', url };
}
